/** Shared ClipToCAD 2.0.0 product copy for landing, FAQ, and structured data. */

export const CLIPTOCAD_VERSION = '2.0.0';
export const CLIPTOCAD_QGIS_RANGE = 'QGIS 3.16–4.x';
export const CLIPTOCAD_GITHUB = 'https://github.com/sifageolabs/cliptocad';
export const CLIPTOCAD_PLUGIN_REPO = 'https://plugins.qgis.org/plugins/cliptocad/';
export const CLIPTOCAD_ISSUES_NEW = 'https://github.com/sifageolabs/cliptocad/issues/new';

export const CLIPTOCAD_TAGLINE =
  'Copy selected QGIS features and paste them into AutoCAD as native command-line text — no DXF, no intermediate files.';

export const CLIPTOCAD_DESCRIPTION =
  'ClipToCAD 2.0.0 copies selected vector features from QGIS 3.16–4.x to the clipboard as AutoCAD _PLINE, _POINT, and _TEXT commands. Multi-layer selections, projected metres, unit presets, and auto parcel-ID labels.';

export const CLIPTOCAD_FAQS = [
  {
    q: 'How do I paste the output into AutoCAD?',
    a: 'Select features in QGIS and click ClipToCAD (or press Ctrl+Shift+C). In AutoCAD press Esc twice so the command line is idle, click inside the Command Line (Ctrl+9), then press Ctrl+V. Real World mode draws at GIS metre coordinates. Local mode prompts for an insertion point.',
  },
  {
    q: 'Why does AutoCAD say “Unknown command” when I paste?',
    a: 'Click inside AutoCAD’s command line before Ctrl+V. If focus is on the drawing canvas, AutoCAD runs PASTECLIP instead of executing command text. Also press Esc twice first so no other command is active.',
  },
  {
    q: 'Does ClipToCAD copy the whole layer if nothing is selected?',
    a: 'No. Version 2.0.0 copies selected features only. A highlighted layer with an empty selection is not copied. Select one feature, many features, or a selection across several vector layers.',
  },
  {
    q: 'How do multi-layer selections land in AutoCAD?',
    a: 'QGIS keeps a selection per layer. ClipToCAD copies every selected feature and emits a _-LAYER switch for each source layer so parcels, roads, and points land on matching CAD layers. All layers are transformed to one destination CRS so they overlay in the DWG.',
  },
  {
    q: 'What is the difference between Real World and Local mode?',
    a: 'Real World pastes exact projected metres (UTM or other projected CRS). It uses only command-line text, so it works on AutoCAD LT. Local mode generates AutoLISP: you click an insertion point and GIS metres are scaled to your drawing units (metric links, Gunter links, feet, yards, inches, or a custom factor). Local needs full AutoCAD or LT 2024+.',
  },
  {
    q: 'Which QGIS versions does ClipToCAD 2.0.0 support?',
    a: 'QGIS 3.16 through 4.x (qgisMaximumVersion 4.99). The plugin is written for both PyQt5 and Qt6 APIs, including GeometryType and LayerType enums used by QGIS 4.',
  },
  {
    q: 'Why is my geographic CRS (EPSG:4326) blocked?',
    a: 'ClipToCAD refuses latitude/longitude output. AutoCAD polylines need projected metres. Set a projected Target EPSG such as 32644 (UTM 44N) or reproject the layer first. Web Mercator (EPSG:3857) is allowed because it is projected metres — though distances are distorted.',
  },
  {
    q: 'How do parcel-ID labels work? Why did FID stop appearing?',
    a: 'Leave Parcel ID field on (auto). Each layer uses parcel_id or Sy.No (and a few survey aliases) if that field exists. You do not visit Settings per layer. A named field is used only on layers that have it. FID is never copied as a label. Labels are drawn as _TEXT on the CAD layer PARCEL_ID.',
  },
  {
    q: 'Large selections fail or freeze AutoCAD. What should I do?',
    a: 'Selections over 200 features or 5,000 vertices are written to cliptocad_last.scr in the plugin folder instead of flooding the clipboard. In AutoCAD type SCRIPT and pick that file.',
  },
  {
    q: 'The plugin folder is named cliptocad-2.0.0 and QGIS cannot load it. Why?',
    a: 'QGIS imports the folder name as the Python package. The folder inside plugins must be named exactly cliptocad — not cliptocad-2.0.0 or cliptocad-2. Copy or extract so the path ends with python/plugins/cliptocad and restart QGIS fully.',
  },
  {
    q: 'Vertices snap to nearby linework when I paste. How do I stop that?',
    a: 'Real World paste temporarily sets OSNAPCOORD to 1 so typed coordinates are not pulled onto nearby geometry, then restores AutoCAD’s default (2). You do not need to turn F3 off.',
  },
  {
    q: 'How do I install ClipToCAD 2.0.0?',
    a: 'Preferred: Plugins → Manage and Install Plugins → search ClipToCAD. Alternative: copy the cliptocad folder into your QGIS profile plugins directory. On Windows that is %APPDATA%\\QGIS\\QGIS3\\profiles\\default\\python\\plugins\\cliptocad. Install from ZIP also works if your antivirus allows Python zip files.',
  },
] as const;
